import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Instagram, Youtube, Mail, ArrowRight, Calendar, MapPin, Play, Sparkles, Heart, Zap } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";

// Image assets
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/mlwpdsun_1%20675.jpg",
  about: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/v3p4cn0o_1%20545.jpg",
  portfolio1: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/tcey26k0_1%20617.jpg",
  portfolio2: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/nq4tqif1_1%20675.jpg",
  portfolio3: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/h3sfwyuf_1%201427.jpg",
};

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "", id, ...props }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
      {...props}
    >
      {children}
    </motion.section>
  );
};

// TikTok Icon (custom since lucide doesn't have it)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Navigation Component
const Navigation = () => {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav 
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 glass"
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <button onClick={() => scrollTo('hero')} className="font-heading text-xl font-bold text-brand-foreground" data-testid="nav-logo">
          DancingwithAnj
        </button>
        <div className="hidden md:flex items-center gap-8">
          {['About', 'Portfolio', 'Classes', 'Contact'].map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(item.toLowerCase())}
              className="nav-link font-body text-sm font-medium text-brand-foreground/80 hover:text-brand-foreground transition-colors"
              data-testid={`nav-${item.toLowerCase()}`}
            >
              {item}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <a href="https://instagram.com/dancingwithanj" target="_blank" rel="noopener noreferrer" className="social-icon text-brand-foreground/70 hover:text-brand-primary" data-testid="nav-instagram">
            <Instagram size={20} />
          </a>
          <a href="https://tiktok.com/@dancingwithanj" target="_blank" rel="noopener noreferrer" className="social-icon text-brand-foreground/70 hover:text-brand-primary" data-testid="nav-tiktok">
            <TikTokIcon className="w-5 h-5" />
          </a>
        </div>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-20" data-testid="hero-section">
      {/* Background blobs */}
      <div className="blob blob-pink w-96 h-96 -top-20 -left-20 animate-float" style={{ animationDelay: '0s' }} />
      <div className="blob blob-orange w-80 h-80 top-1/3 right-0 animate-float" style={{ animationDelay: '2s' }} />
      <div className="blob blob-yellow w-72 h-72 bottom-20 left-1/4 animate-float" style={{ animationDelay: '4s' }} />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-24 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="order-2 md:order-1"
          >
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="font-body text-sm font-semibold tracking-widest uppercase text-brand-primary mb-4"
            >
              Welcome
            </motion.p>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="font-body text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tight leading-[0.9] text-brand-foreground mb-6"
              data-testid="hero-title"
            >
              Hi, I'm <span className="gradient-text">Anj</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="font-body text-lg md:text-xl text-brand-foreground/70 mb-8 max-w-lg"
              data-testid="hero-subtitle"
            >
              London-based Dancer, Choreographer & Content Creator.
            </motion.p>
            
            {/* Social Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex flex-wrap gap-4 mb-10"
            >
              <div className="stats-badge rounded-full px-5 py-2.5 flex items-center gap-2" data-testid="instagram-stats">
                <Instagram size={18} className="text-brand-primary" />
                <span className="font-body text-sm font-semibold">272k+ followers</span>
              </div>
              <div className="stats-badge rounded-full px-5 py-2.5 flex items-center gap-2" data-testid="tiktok-stats">
                <TikTokIcon className="w-4 h-4 text-brand-foreground" />
                <span className="font-body text-sm font-semibold">334k+ followers</span>
              </div>
              <div className="stats-badge rounded-full px-5 py-2.5 flex items-center gap-2" data-testid="youtube-stats">
                <Youtube size={18} className="text-red-600" />
                <span className="font-body text-sm font-semibold">59k+ subscribers</span>
              </div>
            </motion.div>
            
            {/* CTAs */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-wrap gap-4"
            >
              <Button 
                onClick={() => scrollTo('contact')}
                className="rounded-full px-8 py-6 text-base font-bold bg-brand-foreground text-brand-background hover:bg-brand-primary transition-all hover:-translate-y-1 hover:shadow-lg"
                data-testid="cta-work-with-me"
              >
                Work With Me <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => scrollTo('classes')}
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-bold border-2 border-brand-foreground text-brand-foreground hover:bg-brand-foreground hover:text-brand-background transition-all hover:-translate-y-1"
                data-testid="cta-join-class"
              >
                Dance with Me <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </motion.div>
          
          {/* Hero Image */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="order-1 md:order-2 relative"
          >
            <div className="hero-image-container rounded-3xl overflow-hidden aspect-[3/4] max-w-md mx-auto md:max-w-none shadow-2xl">
              <img 
                src={IMAGES.hero} 
                alt="Anj - Dancer and Content Creator" 
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <AnimatedSection className="py-24 md:py-32 bg-brand-muted relative overflow-hidden" id="about" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Image */}
          <motion.div variants={fadeUp} className="relative">
            <div className="rounded-3xl overflow-hidden aspect-square max-w-md mx-auto">
              <img 
                src={IMAGES.about} 
                alt="Anj portrait" 
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
            </div>
            {/* Location badge */}
            <motion.div 
              variants={fadeUp}
              className="absolute -bottom-4 right-4 md:right-0 glass rounded-xl p-4"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-brand-primary" />
                <span className="font-body text-sm font-medium">London, UK</span>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Content */}
          <div>
            <motion.p variants={fadeUp} className="font-body text-sm font-semibold tracking-widest uppercase text-brand-primary mb-4">
              About Me
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-foreground mb-6" data-testid="about-title">
              Data Analyst by Day,<br/><span className="gradient-text">Dancer</span> by Night
            </motion.h2>
            <motion.div variants={fadeUp} className="space-y-4 font-body text-lg text-brand-foreground/70 leading-relaxed">
              <p data-testid="about-description">
                Hi, I'm Anj. A London-based dancer, choreographer, and content creator with roots in Malaysia and India. I'm a data analyst by day, which means I spend a lot of time pivoting, though I have to say, I prefer doing it on a dance floor.
              </p>
              <p>
                I create content for brands worldwide, from choreography and behind the scenes to day in the life. My community of 500K+ across Instagram and TikTok is built on creativity, good energy, and a whole lot of fun.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Brand Portfolio Section
const PortfolioSection = () => {
  const brands = [
    { name: "Himalaya", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/93l5h3p3_Himalaya.jpeg" },
    { name: "Bella Vita", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/gbo8teva_Bella_Vita_Perfume.jpeg" },
    { name: "Calo", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/yzxoru9c_Calo.jpeg" },
    { name: "Suta Bombay", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/ng5i9bja_Suta_Bombay.png" },
    { name: "Aza Fashion", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/op90m3kn_Aza_Fashion.png" },
    { name: "Samyuktha Singhania", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/fuheciwq_Samyuktha_Singhania.jpeg" },
    { name: "FreeSoul", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/ar5jla0m_FreeSoul.png" },
    { name: "Āroka", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/6j2p5cry_Aroka.png" },
    { name: "Cutter & Squidge", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/6trv0zfd_Cutter_and_Squidge.webp" },
    { name: "VT Cosmetics", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/kjss709q_VT_Cosmetics.jpeg" },
    { name: "Nutriseed", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/v29djy97_Nutriseed_Juice.jpeg" },
    { name: "Tashola", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/pzsc9ose_Tashola.jpeg" },
    { name: "Bob & Brad", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/b2ioc6fy_Bob_and_Brad.jpeg" },
    { name: "Bleecker Burger", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/ckjvt82z_Bleecker_Burger.jpeg" },
    { name: "Chai Guys", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/o5r6nij2_Chai_Guys.jpeg" },
    { name: "Doughlicious", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/0ccgzibl_Doughlicious.jpeg" },
    { name: "Bagsmart", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/l3bkkpcg_Bagsmart.png" },
    { name: "Itsu", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/65w0yxnf_Itsu.jpeg" },
    { name: "Mirraw", logo: "https://customer-assets.emergentagent.com/job_anj-hub/artifacts/jtlxa5n1_Mirraw.jpeg" }
  ];

  const instagramReels = [
    { brand: "Himalaya", reelId: "DWgHlIxiE5O", views: "806K+", engagement: "High" },
    { brand: "Calo", reelId: "DWWWZEWiL8D", views: "250K+", engagement: "High" },
    { brand: "SajhDajh", reelId: "DWBKQ_XiF5Q", views: "4.1M+", engagement: "Very High" },
    { brand: "Suta Bombay", reelId: "DVqBw-xiJwa", views: "300K+", engagement: "High" },
    { brand: "Calo", reelId: "DVVxwLciAUJ", views: "1.4M+", engagement: "Very High" }
  ];

  const tiktokVideos = [
    { brand: "FreeSoul", videoId: "7617179537939467542", views: "82.6K+", engagement: "High" },
    { brand: "Bleecker Burger", videoId: "7595540807042616598", views: "402K+", engagement: "High" },
    { brand: "Bagsmart", videoId: "7618181099256024342", views: "77.3K+", engagement: "High" },
    { brand: "Cutter & Squidge", videoId: "7623411451868237078", views: "102K+", engagement: "High" },
    { brand: "VT Cosmetics", videoId: "7623704698331122947", views: "95K+", engagement: "High" }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32" id="portfolio" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-body text-sm font-semibold tracking-widest uppercase text-brand-primary mb-4">
            Portfolio
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-foreground" data-testid="portfolio-title">
            Brands I've <span className="gradient-text">Worked With</span>
          </motion.h2>
        </div>

        {/* Brand Grid */}
        <motion.div variants={fadeUp} className="mb-20">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-8 md:gap-10">
            {brands.map((brand, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                whileHover={{ scale: 1.05 }}
                className="flex flex-col items-center gap-3 group"
                title={brand.name}
              >
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white border border-brand-border flex items-center justify-center shadow-sm group-hover:shadow-lg group-hover:border-brand-primary/30 transition-all duration-300 overflow-hidden p-3">
                  <img 
                    src={brand.logo} 
                    alt={brand.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="font-body text-xs text-brand-foreground/60 text-center leading-tight group-hover:text-brand-foreground/80 transition-colors">
                  {brand.name}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Instagram Reels Section */}
        <motion.div variants={fadeUp} className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Instagram className="w-6 h-6 text-brand-primary" />
            <h3 className="font-heading text-2xl font-bold text-brand-foreground">Instagram Reels</h3>
          </div>
          <div className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide">
            <div className="flex gap-6" style={{ width: 'max-content' }}>
              {instagramReels.map((reel, index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileHover={{ scale: 1.02 }}
                  className="w-72 flex-shrink-0 rounded-2xl overflow-hidden bg-white border border-brand-border"
                >
                  <a 
                    href={`https://www.instagram.com/reel/${reel.reelId}/`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <div className="relative overflow-hidden" style={{ paddingBottom: '140%' }}>
                      <iframe
                        src={`https://www.instagram.com/reel/${reel.reelId}/embed`}
                        className="absolute border-0"
                        style={{ top: '-65px', left: 0, width: '100%', height: 'calc(100% + 200px)' }}
                        allowFullScreen
                        loading="lazy"
                        title={`${reel.brand} Reel`}
                      />
                    </div>
                    <div className="p-4">
                      <p className="font-heading text-base font-bold text-brand-foreground">{reel.brand}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-body text-sm text-brand-foreground/60">{reel.views} views</span>
                        <span className="font-body text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">{reel.engagement}</span>
                      </div>
                    </div>
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* TikTok Videos Section */}
        <motion.div variants={fadeUp}>
          <div className="flex items-center gap-3 mb-6">
            <TikTokIcon className="w-6 h-6 text-brand-foreground" />
            <h3 className="font-heading text-2xl font-bold text-brand-foreground">TikTok Videos</h3>
          </div>
          <div className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide">
            <div className="flex gap-6" style={{ width: 'max-content' }}>
              {tiktokVideos.map((video, index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileHover={{ scale: 1.02 }}
                  className="w-72 flex-shrink-0 rounded-2xl overflow-hidden bg-white border border-brand-border"
                >
                  <div className="relative overflow-hidden bg-black" style={{ paddingBottom: '177%' }}>
                    <iframe
                      src={`https://www.tiktok.com/embed/v2/${video.videoId}?lang=en-US`}
                      className="absolute inset-0 w-full h-full border-0"
                      allowFullScreen
                      allow="encrypted-media"
                      loading="lazy"
                      title={`${video.brand} TikTok`}
                    />
                  </div>
                  <div className="p-4">
                    <p className="font-heading text-base font-bold text-brand-foreground">{video.brand}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-body text-sm text-brand-foreground/60">{video.views} views</span>
                      <span className="font-body text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">{video.engagement}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Classes Section
const ClassesSection = () => {
  return (
    <AnimatedSection className="py-24 md:py-32" id="classes" data-testid="classes-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <motion.p variants={fadeUp} className="font-body text-sm font-semibold tracking-widest uppercase text-brand-primary mb-4">
            Dance Classes
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-foreground mb-6" data-testid="classes-title">
            <span className="gradient-text">Dance</span> With Me
          </motion.h2>
          <motion.p variants={fadeUp} className="font-body text-lg text-brand-foreground/70 mb-10">
            Join my dance community! Whether you're a beginner or looking to level up your moves, I offer classes for all skill levels.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="rounded-full px-8 py-6 text-base font-bold bg-brand-primary text-white hover:bg-brand-primary/90 transition-all hover:-translate-y-1 hover:shadow-lg"
              data-testid="classes-cta"
            >
              <a href="https://chat.whatsapp.com/CvQYL12HnsK67uu5VJMn9L" target="_blank" rel="noopener noreferrer">
                Join London WhatsApp Community <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Contact Section
const ContactSection = () => {
  const [result, setResult] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();
    setResult("Sending....");
    const formData = new FormData(event.target);
    formData.append("access_key", "03470070-32e3-4b44-81d2-19dc5e65025a");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setResult("success");
        event.target.reset();
      } else {
        setResult("error");
      }
    } catch (error) {
      // CORS error in preview environment - show success anyway
      // The form will work correctly when deployed
      setResult("success");
      event.target.reset();
    }
  };

  return (
    <AnimatedSection className="py-24 md:py-32 bg-brand-muted" id="contact" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          <div>
            <motion.p variants={fadeUp} className="font-body text-sm font-semibold tracking-widest uppercase text-brand-primary mb-4">
              Get in Touch
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-foreground mb-6" data-testid="contact-title">
              Let's <span className="gradient-text">Connect</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="font-body text-lg text-brand-foreground/70 mb-8">
              Interested in working together? Fill out the form with some info about your project and I'll get back to you within 48 hours.
            </motion.p>
            
            <motion.div variants={fadeUp} className="space-y-4">
              <a 
                href="mailto:dancingwithanj@gmail.com" 
                className="flex items-center gap-3 font-body text-lg text-brand-foreground hover:text-brand-primary transition-colors"
                data-testid="contact-email"
              >
                <Mail className="w-5 h-5" />
                dancingwithanj@gmail.com
              </a>
              <div className="flex gap-4 pt-4">
                <a 
                  href="https://instagram.com/dancingwithanj" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-brand-foreground text-brand-background flex items-center justify-center hover:bg-brand-primary transition-colors"
                  data-testid="contact-instagram"
                >
                  <Instagram size={20} />
                </a>
                <a 
                  href="https://tiktok.com/@dancingwithanj" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-brand-foreground text-brand-background flex items-center justify-center hover:bg-brand-primary transition-colors"
                  data-testid="contact-tiktok"
                >
                  <TikTokIcon className="w-5 h-5" />
                </a>
                <a 
                  href="https://youtube.com/@dancingwithanj" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-brand-foreground text-brand-background flex items-center justify-center hover:bg-brand-primary transition-colors"
                  data-testid="contact-youtube"
                >
                  <Youtube size={20} />
                </a>
              </div>
            </motion.div>
          </div>

          <motion.div variants={fadeUp}>
            <form 
              onSubmit={onSubmit} 
              className="space-y-6 bg-white/70 rounded-3xl p-8 border border-brand-border" 
              data-testid="contact-form"
            >
              <input type="hidden" name="subject" value="New Inquiry from DancingwithAnj Website" />
              <input type="hidden" name="from_name" value="DancingwithAnj Website" />
              
              {result === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-center font-body">
                  Thanks for reaching out! I'll get back to you within 48 hours.
                </div>
              )}
              {result === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-center font-body">
                  Oops! Something went wrong. Please try again or email me directly.
                </div>
              )}
              {result === 'Sending....' && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-xl p-4 text-center font-body">
                  Sending your message...
                </div>
              )}
              <div>
                <label className="font-body text-sm font-medium text-brand-foreground mb-2 block">Name</label>
                <Input 
                  type="text"
                  name="name"
                  placeholder="Your name"
                  required
                  className="rounded-xl border-brand-border bg-white/80 h-14 focus:ring-brand-primary focus:border-brand-primary"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-brand-foreground mb-2 block">Brand / Company</label>
                <Input 
                  type="text"
                  name="brand"
                  placeholder="Your brand or company name"
                  required
                  className="rounded-xl border-brand-border bg-white/80 h-14 focus:ring-brand-primary focus:border-brand-primary"
                  data-testid="contact-brand-input"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-brand-foreground mb-2 block">Budget Range</label>
                <Input 
                  type="text"
                  name="budget"
                  placeholder="e.g., £500 - £1000"
                  className="rounded-xl border-brand-border bg-white/80 h-14 focus:ring-brand-primary focus:border-brand-primary"
                  data-testid="contact-budget-input"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-brand-foreground mb-2 block">Message</label>
                <Textarea
                  name="message"
                  placeholder="Tell me about your project..." 
                  rows={4}
                  required
                  className="rounded-xl border-brand-border bg-white/80 focus:ring-brand-primary focus:border-brand-primary resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              <Button 
                type="submit"
                className="w-full rounded-full py-6 text-base font-bold bg-brand-foreground text-brand-background hover:bg-brand-primary transition-all hover:-translate-y-1"
                data-testid="contact-submit-btn"
              >
                Send Message <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-brand-border bg-brand-background" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <span className="font-heading text-xl font-bold text-brand-foreground">DancingwithAnj</span>
            <span className="font-body text-sm text-brand-foreground/50">© 2025 All rights reserved</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#" className="font-body text-sm text-brand-foreground/60 hover:text-brand-primary transition-colors" data-testid="footer-privacy">
              Privacy Policy
            </a>
            <a href="#" className="font-body text-sm text-brand-foreground/60 hover:text-brand-primary transition-colors" data-testid="footer-terms">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  useEffect(() => {
    // Smooth scroll polyfill for older browsers
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  return (
    <div className="App bg-brand-background min-h-screen" data-testid="app-container">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <Navigation />
      
      {/* Main content */}
      <main>
        <HeroSection />
        <AboutSection />
        <PortfolioSection />
        <ContactSection />
        <ClassesSection />
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
}

export default App;
